import { collection, getFirestore, onSnapshot } from "firebase/firestore";
import { type FirestoreTextSearchControllerBuilder } from "@firecms/firebase";
import { type Tag } from "@one-ai/shared/cms/schema";
import { streams } from "@one-ai/shared/cms/streams";

/**
 * Search for the `tags` collection — the search bar on its listing view, and
 * the one in the news article's tag picker. Both are the same bar: FireCMS
 * reads `textSearchEnabled` off the collection, and `EntityCollectionView` and
 * `ReferenceSelectionInner` both build it from there.
 *
 * **Why this exists rather than `localTextSearchEnabled`.** FireCMS ships a
 * local controller in `useFirestoreDelegate`, and it cannot search a tag. Its
 * Fuse index takes `["id", ...Object.keys(collection.properties)]` — top-level
 * keys only — and `label` is a `map`, so what it would index is `date`,
 * `retired`, `order` and the id, i.e. everything about a tag except the two
 * strings an editor knows it by. Its `threshold: 0.6` fuzzy scoring is also the
 * wrong instrument for 繁體中文 and 日本語, where an editor typing 開發 means the
 * tag containing 開發 and nothing near it.
 *
 * Contains-matching over both locales plus the id is the whole feature. There
 * are tens of tags, not thousands.
 *
 * **Retired tags are returned.** Finding one is the point on the listing view —
 * un-retiring a tag starts with locating it. Keeping one out of the news
 * article's picker is a different job, done by the `forceFilter` on that
 * reference property and `lib/searchFilter.ts`, which is what puts that filter
 * back after the search branch drops it.
 */

/**
 * A tag as a Firestore snapshot hands it over — **not** a parsed `Tag`.
 *
 * Nothing `safeParse`s these on the way into the index, so every field is
 * optional and none is trustworthy: a tag `NewTagAction` has just written and
 * the editor has not filled in yet is a real document with no `label`, and it
 * must not take the search bar down with it. `searchTags` type-checks each
 * value it reads for that reason.
 */
export type TagDoc = Partial<Tag> & { id: string };

/** More rows than this in a picker is scrolling, not searching. */
const MAX_RESULTS = 50;

/**
 * Fullwidth-folded, case-folded. NFKC is what makes ＭＡＳＡ — which is simply
 * what a Japanese IME produces unless the editor thinks about it — find the
 * same tag MASA does.
 */
const normalize = (value: string) => value.normalize("NFKC").toLowerCase();

const isNumericId = (id: string) => /^\d+$/.test(id);

/**
 * Numbers first and in numeric order, hand-written slugs after them
 * alphabetically. Ids are assigned as whole numbers (`nextSlug.ts`), so a
 * string sort would file "10" under "1" — the same bug `nextNumericSlug`
 * guards against. Tags created before 2026-09-08 keep a written slug and have
 * no place in that sequence, so they sort below it.
 */
const compareIds = (a: string, b: string) => {
    const aNumeric = isNumericId(a);
    const bNumeric = isNumericId(b);
    if (aNumeric && bNumeric) return Number(a) - Number(b);
    if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
    return a.localeCompare(b);
};

/**
 * The ids of the tags matching `searchString`, best first.
 *
 * Pure, and separate from the controller below, because this is the part with
 * decisions in it.
 *
 * Retirement is deliberately not consulted here — see the note at the top of
 * this file, and `lib/searchFilter.ts` for what keeps a retired tag out of the
 * picker.
 */
export const searchTags = (docs: TagDoc[], searchString: string): string[] => {
    const query = normalize(searchString).trim();

    const matches = docs.filter((doc) => {
        if (!query) return true;
        const haystacks = [
            doc.id,
            doc.label?.zh,
            doc.label?.jp,
            // Every other field is a date, a flag or a number: nothing an
            // editor searches a tag by, and `date` in particular would match
            // a bare "2" on almost every record.
        ].filter((value): value is string => typeof value === "string");
        return haystacks.some((value) => normalize(value).includes(query));
    });

    return matches
        .sort((a, b) => {
            // A fully typed id is unambiguous — the editor knows which tag they
            // want and `order` has no opinion worth hearing.
            const aExact = normalize(a.id) === query;
            const bExact = normalize(b.id) === query;
            if (aExact !== bExact) return aExact ? -1 : 1;

            // `order` is what the public filter bar sorts on, so the picker
            // agrees with the site the editor is looking at. Unordered tags
            // fall to the bottom rather than to the top, which is where a
            // missing number would otherwise put them.
            const aOrder = typeof a.order === "number" ? a.order : Infinity;
            const bOrder = typeof b.order === "number" ? b.order : Infinity;
            if (aOrder !== bOrder) return aOrder - bOrder;

            return compareIds(a.id, b.id);
        })
        .slice(0, MAX_RESULTS)
        .map((doc) => doc.id);
};

/**
 * Every tag collection this controller answers for — one per stream.
 *
 * Derived from `streams` rather than listed, so a new stream is searchable the
 * moment it exists. The set is what `init` checks; a path outside it is
 * reported unsupported, which is what makes turning `textSearchEnabled` on for
 * an ARTICLE collection fail visibly instead of quietly answering it with tag
 * ids.
 */
const TAG_PATHS = new Set(streams.map((stream) => stream.tagCollection));

/**
 * The controller `App.tsx` hands `useFirestoreDelegate`.
 *
 * A module-level const, not something built in a component: the delegate takes
 * it as a `useMemo` dependency, and a new function identity on every render
 * would rebuild the data source under the whole CMS.
 *
 * `init` resolves `false` for any path outside `TAG_PATHS`, which is what makes
 * FireCMS hide the search bar rather than show one that answers nothing — so
 * switching another collection to `textSearchEnabled` fails visibly here
 * instead of silently returning tag ids.
 */
export const tagSearchControllerBuilder: FirestoreTextSearchControllerBuilder =
    ({ firebaseApp }) => {
        /**
         * One index per tag collection, each kept live by its own
         * `onSnapshot`.
         *
         * Listeners rather than a `getDocs` per search because a tag an editor
         * just created has to be findable in the picker they open next, and
         * because the collections are small enough that holding them costs
         * nothing. They are deliberately never torn down: the controller
         * outlives every view that searches through it, and FireCMS gives it no
         * destroy hook.
         *
         * Keyed by path rather than shared, because the two collections hold
         * different documents under the same ids — one merged index would
         * answer a picker with the other stream's tags.
         */
        const docsByPath = new Map<string, TagDoc[]>();
        const subscriptions = new Map<string, () => void>();

        const init = ({ path }: { path: string }) => {
            if (!TAG_PATHS.has(path)) return Promise.resolve(false);
            if (subscriptions.has(path)) return Promise.resolve(true);

            return new Promise<boolean>((resolve, reject) => {
                subscriptions.set(
                    path,
                    onSnapshot(collection(getFirestore(firebaseApp), path), {
                        next: (snapshot) => {
                            docsByPath.set(
                                path,
                                snapshot.docs.map((doc) => ({
                                    id: doc.id,
                                    ...doc.data(),
                                })) as TagDoc[],
                            );
                            // Resolving on the first snapshot is what tells
                            // FireCMS the bar is ready; later ones just refresh
                            // the index, and resolving twice is a no-op.
                            resolve(true);
                        },
                        error: (error) => {
                            // Leave nothing half-open: a failed listener that
                            // stays assigned would make the next `init` report
                            // an index it does not have.
                            subscriptions.get(path)?.();
                            subscriptions.delete(path);
                            reject(error);
                        },
                    }),
                );
            });
        };

        const search = ({
            searchString,
            path,
        }: {
            searchString: string;
            path: string;
        }) =>
            Promise.resolve(
                TAG_PATHS.has(path)
                    ? searchTags(docsByPath.get(path) ?? [], searchString)
                    : undefined,
            );

        return { init, search };
    };
